import React, { useState, useEffect } from 'react';

import { useLocalization } from '@progress/kendo-react-intl';
import { useHistory, useParams } from "react-router-dom";
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Error } from "@progress/kendo-react-labels";
import { emailValidator, passwordValidator, passwordMatchValidator } from '../validators';
import logo from "../assets/images/Ford_BuildingTogether_Blue_Screen_RGB_Vt_v2.png"
// @ts-ignore
import PrivacyBanner from 'react-ford-ccpa-banner';
import { AppContext } from '../AppContext'
import { useServices } from 'src/services';
import { useStores } from 'src/stores';
import { Input } from 'src/components/form/Input';
import sideImage from "../assets/images/2025_D4URC_Dealer_Portal_Forgot_Password_Page_66836.png";
import '../styles/_login.scss';
import NotFound from './NotFound';

const UpdateContactEmailScreen = () => {
    const { ...formValues } = React.useContext(AppContext);
    const localizationService = useLocalization();
    const history = useHistory();
    const { language } = localizationService;
    const [error, setError] = useState<string>();
    const [confirmMessage, setConfirmMessage] = useState<string>('');
    const [loginCredentials, setLoginCredentials] = useState<{ email: string; password: string } | null>(null);
    const { d4ur } = useServices();
    const { login } = useStores();
    // @ts-ignore
    const { token } = useParams();

    useEffect(() => {
        if (!token) {
            history.push('/404');
        }
    }, [token, history]);

    const onSubmit = React.useCallback(
        async (dataItem) => {
            if (!token) {
                setError('Invalid or missing token');
                return;
            }

            setError('');
            const call: any = await d4ur.updateContactEmailAndPassword(
                dataItem.contact_email,
                dataItem.password,
                token
            );

            if (call.result && call.result.error) {
                console.log('[UpdateContactEmailScreen]', 'Update Error');
                setError(call.result.error);
            } else if (call.result && call.result.success) {
                console.log('[UpdateContactEmailScreen]', 'Contact email and password updated successfully');
                setConfirmMessage('Contact email and password updated successfully');
                // Store credentials for auto-login
                setLoginCredentials({ email: dataItem.contact_email, password: dataItem.password });
            } else {
                if (call.result && call.result.result) {
                    if (call.result.result.error) {
                        setError(call.result.result.error);
                    } else if (call.result.result.success) {
                        setConfirmMessage('Contact email and password updated successfully');
                        // Store credentials for auto-login
                        setLoginCredentials({ email: dataItem.contact_email, password: dataItem.password });
                    } else {
                        setError('An unexpected error occurred. Please try again.');
                    }
                } else {
                    console.error('[UpdateContactEmailScreen]', 'Unexpected response structure:', call);
                    setError('An unexpected error occurred. Please try again.');
                }
            }
        },
        [token, d4ur]
    );

    const handleLogin = React.useCallback(
        async () => {
            if (!loginCredentials) {
                setError('Unable to login. Please use the login page.');
                return;
            }

            setError('');
            login.setLoginEmail(loginCredentials.email);
            const call: any = await d4ur.doLogin(loginCredentials.email, loginCredentials.password);
            
            if (call.result && call.result.ERROR) {
                console.log('[UpdateContactEmailScreen]', 'Login Error');
                setError(call.result.message || 'Login failed. Please try logging in manually.');
            } else if (call.result && call.result.dealer) {
                console.log('[UpdateContactEmailScreen]', 'Logged in successfully');
                login.setDealerToken(call.result.token);
                login.setDealer(call.result.dealer);
                
                // Check if this is the dealer's first login this season
                const dealerId = call.result.dealer.dealer_id;
                const firstLoginKey = `d4ur_first_login_${dealerId}`;
                const hasLoggedInBefore = localStorage.getItem(firstLoginKey);
                
                if (!hasLoggedInBefore) {
                    // Mark as logged in for future sessions
                    localStorage.setItem(firstLoginKey, 'true');
                    // Redirect to profile with first login flag
                    history.push('/profile', { isFirstLogin: true });
                } else {
                    history.push('/events');
                }
            } else {
                setError('Login failed. Please try logging in manually.');
            }
        },
        [loginCredentials, d4ur, login, history]
    );

    // If no token, show 404
    if (!token) {
        return <NotFound />;
    }

    return (
        <div id="UpdateContactEmail" className="forgot_pass">
            <div className='main'>
                <div className="left_content">
                    <img src={logo} alt={'Ford Drive 4 UR School Logo'} className='logo' onClick={() => {
                        history.push("/");
                    }}/>
                    <div className="form-container" style={{ maxWidth: 700 }}>
                        <h1 className="form_title">Update Primary Dealer Contact</h1>
                        <p style={{ marginBottom: '20px', fontSize: '14px' }}>
                            Please enter your new contact email and password for the 2026 season.
                        </p>
                        <Form
                            onSubmit={onSubmit}
                            initialValues={{
                                ...formValues,
                                contact_email: '',
                                password: '',
                                password2: '',
                            }}
                            render={(formRenderProps) => (
                                <FormElement horizontal={true} style={{ maxWidth: 700 }}>
                                    {error && <Error>{error}</Error>}

                                    <Field
                                        id={'contact_email'}
                                        name={'contact_email'}
                                        type={'email'}
                                        autoComplete="username"
                                        placeholder={'e.g.: peter@gmail.com'}
                                        label={'New Contact Email'}
                                        validator={emailValidator}
                                        required={true}
                                        component={Input}
                                    />

                                    <Field
                                        id={'password'}
                                        name={'password'}
                                        autoComplete="new-password"
                                        type={'password'}
                                        label={localizationService.toLanguageString('custom.newPassword', 'New Password')}
                                        validator={passwordValidator}
                                        required={true}
                                        component={Input}
                                    />

                                    <Field
                                        id={'password2'}
                                        name={'password2'}
                                        autoComplete="new-password"
                                        type={'password'}
                                        label={localizationService.toLanguageString('custom.secondPassword', 'Retype Password')}
                                        validator={passwordMatchValidator}
                                        required={true}
                                        component={Input}
                                    />
                                    
                                    <div>
                                        <p className='forgot-text' style={{ color: 'green' }}>{confirmMessage && confirmMessage}</p>
                                        <Button
                                            primary={true}
                                            type={'submit'}
                                            disabled={!formRenderProps.allowSubmit}
                                            hidden={!!confirmMessage}
                                            className='log_in_btn'
                                        >
                                            Update
                                        </Button>
                                    </div>
                                    {confirmMessage && (
                                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                            <Button
                                                primary={true}
                                                className='log_in_btn'
                                                onClick={handleLogin}
                                            >
                                                Log In
                                            </Button>
                                            <Button
                                                primary={true}
                                                className='log_in_btn'
                                                onClick={() => {
                                                    history.push("/login", { skipSalesCode: true });
                                                }}
                                            >
                                                {localizationService.toLanguageString('custom.goBack', 'Go Back')}
                                            </Button>
                                        </div>
                                    )}
                                </FormElement>
                            )}
                        />
                    </div>
                </div>
                <div className='right_content'>
                    <img src={sideImage} alt={'Two volunteers talking next to a Ford vehicle'} className='right_img' />        
                </div>
            </div>
            <PrivacyBanner
                containerStyle={{ padding: '10px 0 0' }}
                privacyContainerStyle={{ padding: '10px 0', width: "60%", marginLeft: '20%' }}
                showPrivacy={true}
                language={language}
                team={'ford'}
                imgStyle={{ paddingLeft: '30px' }}
                otherLinks={[
                    { text: "CONTACT US", url: "/contact", target: '_blank' },
                    { text: "PROGRAM TERMS AND CONDITIONS", url: "/terms-eligibility", target: '_blank'},
                    { text: "PROGRAM RESOURCES ", url: "/resources", target: '_blank' },
                ]}
            />
        </div>
    );
}

export default UpdateContactEmailScreen;